"use client";
import { toRelativeTime } from "../../utils/formatting/date";
import { Badge, type BadgeProps } from "../Badge";
import { Skeleton } from "../Skeleton";

export interface ConnectionStatusBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  /** Whether the device is currently connected */
  isConnected: boolean;
  /** Timestamp when the device last connected */
  connectedAt?: Date | string | null;
  /** Timestamp when the device disconnected */
  disconnectedAt?: Date | string | null;
  /** Show timestamp below the status */
  showTimestamp?: boolean;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * ConnectionStatusBadge
 *
 * Displays device connection status with optional timestamps.
 * - Green "Connected" badge with pulsing dot
 * - Gray "Disconnected" badge with duration offline
 * - Auto-calculates "offline for 2 hours" type strings
 *
 * @example
 * ```tsx
 * <ConnectionStatusBadge isConnected={true} />
 * <ConnectionStatusBadge isConnected={false} disconnectedAt={new Date()} showTimestamp />
 * <ConnectionStatusBadge isConnected={true} connectedAt={new Date()} />
 * ```
 */
export function ConnectionStatusBadge({
  isConnected,
  connectedAt,
  disconnectedAt,
  showTimestamp = false,
  isLoading,
  size = "sm",
  shape = "pill",
  ...badgeProps
}: ConnectionStatusBadgeProps) {
  if (isLoading) {
    return <Skeleton className="inline-flex h-6 w-24" />;
  }

  const label = isConnected ? "Connected" : "Disconnected";
  const variant = isConnected ? "success" : "neutral";

  // Calculate duration string for disconnected state
  let timestampText: string | null = null;
  if (showTimestamp) {
    if (isConnected && connectedAt) {
      timestampText = toRelativeTime(connectedAt);
    } else if (!isConnected && disconnectedAt) {
      const relative = toRelativeTime(disconnectedAt);
      timestampText = relative ? `Offline ${relative}` : null;
    }
  }

  return (
    <div className="inline-flex flex-col gap-1">
      <Badge variant={variant} size={size} shape={shape} dot={isConnected} dotPulse={isConnected} {...badgeProps}>
        {label}
      </Badge>
      {timestampText && <span className="text-xs text-text-secondary">{timestampText}</span>}
    </div>
  );
}
